import {
  type AddEntryEventSource,
  getDatabaseContainerType,
  useAnalytics
} from '@databases/analytics';
import { collabStateManager } from '@databases/collab-state-manager';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { inlineRovoPromptState } from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import LayoutResizeObserver from '@databases/layout/observer/ResizeObserver';
import {
  useScrollPosition,
  useWindowScrollPosition
} from '@databases/layout/scroll-position';
import { isNativeEmbed, isNativeEmbedEditorPlugin } from '@databases/modes';
import { NativeEmbedSelectionBorder } from '@databases/native-embed-editor-plugin/SelectionBorder';
import { getPinnedLeadingAddEntryScrollLeft } from '@databases/pinned-columns/pinned-leading-add-entry-scroll';
import { useResizeObserver } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getService } from '@databases/service-container';
import { type TimelineLayoutProps } from '@databases/timeline-layout';
import { type DatabaseEntry } from '@databases/types/database';
import {
  type DatabaseViewSettings,
  DatabaseLayoutType
} from '@databases/types/database/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { AiSummaryCellHeightObserverProvider } from '@databases/value/ai-summary-cell-height-observer';
import { TABLE_V2_LAYOUT_ID } from '@databases/view-layout-table-v2/id';
import useView from '@databases/views/useView';
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { useSnapshot } from 'valtio';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';
import { dynamicImport } from '@canvas/dynamic-import';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';

import { DatabaseBoardSkeleton } from '../database-skeletons/DatabaseBoardSkeleton.tsx';
import { DatabaseGallerySkeleton } from '../database-skeletons/DatabaseGallerySkeleton.tsx';
import { DatabaseTableSkeletonRefresh } from '../database-skeletons/DatabaseTableSkeletonRefresh.tsx';

import { DatabaseAiConfigModalProvider } from './ai-config/database-ai-config-modal-provider.tsx';
import styles from './database-layout-renderer.module.css';
import { DatabaseTableSkeleton } from './DatabaseContentSkeleton.tsx';
import DatabaseContentWrapper from './DatabaseContentWrapper.ts';
import {
  ProgressiveEntryRevealProvider,
  useProgressiveEntryRevealContext
} from './hooks/progressive-entry-reveal-context.tsx';
import { ProgressiveFieldAddRevealProvider } from './hooks/progressive-field-add-reveal-context.tsx';
import { ProgressiveFieldDeleteRevealProvider } from './hooks/progressive-field-delete-reveal-context.tsx';
import { ProgressiveFieldEditRevealProvider } from './hooks/progressive-field-edit-reveal-context.tsx';
import { useBackendAgentEntryPresence } from './hooks/use-backend-agent-entry-presence.ts';
import { useBackendAgentFieldDeleteOverlay } from './hooks/use-backend-agent-field-delete-overlay.ts';
import { useBackendAgentFieldEditPresence } from './hooks/use-backend-agent-field-edit-presence.ts';
import { useProgressiveEntryDeleteReveal } from './hooks/use-progressive-entry-delete-reveal.ts';
import { useProgressiveEntryEditReveal } from './hooks/use-progressive-entry-edit-reveal.ts';
import {
  type ProgressiveEntryRevealLifecycleEvent,
  useProgressiveEntryReveal
} from './hooks/use-progressive-entry-reveal.ts';
import { useProgressiveFieldAddReveal } from './hooks/use-progressive-field-add-reveal.ts';
import {
  hasStructuralEntryRevealIndexMismatch,
  useStructuralEntryRevealPresentation
} from './hooks/use-structural-entry-reveal-presentation.ts';
import { resolveExtensionLayoutRenderer } from './resolve-extension-layout-renderer.ts';
import { TableV2Host } from './table-v2-host.tsx';

/** While scrolling, render the rows in batches of this height, rather than rendering each row on its own. */
const RENDER_BATCH_HEIGHT = 500;
/** Render rows this amount of pixels above and below the visible section as well. */
const RENDER_MARGIN = 800;
/** Tolerance in pixels when comparing table width to container width, to prevent redundant scroll bar. */
const SCROLL_WIDTH_TOLERANCE_PX = 4;

const TableLayout = lazy(() =>
  dynamicImport(() => import('./table-layout/TableLayout.tsx'))
);
const BoardLayout = lazy(() =>
  dynamicImport(() => import('./board-layout/BoardLayout.tsx'))
);
const CalendarLayout = lazy(() =>
  dynamicImport(() => import('./calendar-layout/CalendarLayout.tsx'))
);
const GalleryLayout = lazy(() =>
  dynamicImport(() => import('./gallery-layout/GalleryLayout.tsx'))
);
const TimelineLayout = lazy(() =>
  dynamicImport(() => import('@databases/timeline-layout'))
);
const InlineRovoPrompt = lazy(() =>
  dynamicImport(
    () => import('@databases/database-ai-first-creation-ui/InlineRovoPrompt')
  ).then(({ InlineRovoPrompt }) => ({ default: InlineRovoPrompt }))
);

const InlineRovoPromptLoader = ({ container }: { container: HTMLElement }) => {
  const { isOpen } = useSnapshot(inlineRovoPromptState);

  return isOpen ? (
    <Suspense fallback={null}>
      <InlineRovoPrompt container={container} />
    </Suspense>
  ) : null;
};

const extensionLayoutRenderers = new Map([[TABLE_V2_LAYOUT_ID, TableV2Host]]);

type EditingIds = Set<{ entryId: string; fieldId: string | undefined }>;

function getProgressiveEntryRevealAnalyticsSource(
  layoutType: DatabaseLayoutType
): AddEntryEventSource | undefined {
  switch (layoutType) {
    case DatabaseLayoutType.TABLE:
      return 'tableLayout';
    case DatabaseLayoutType.BOARD:
      return 'boardLayout';
    case DatabaseLayoutType.GALLERY:
      return 'galleryLayout';
    case DatabaseLayoutType.TIMELINE:
      return 'timelineLayout';
  }

  return undefined;
}

function ProgressiveEntryRevealOwner({
  analyticsSource,
  children,
  contentId,
  editingIds,
  layoutRoot,
  viewId,
  viewSettings
}: {
  analyticsSource: AddEntryEventSource;
  children: React.ReactNode;
  contentId: string;
  editingIds: EditingIds;
  layoutRoot: HTMLElement | null;
  viewId: string;
  viewSettings: DatabaseViewSettings;
}) {
  const subscribeToCollabManager = useCallback(
    (onStoreChange: () => void) =>
      collabStateManager.onCollabManagerAttached(contentId, onStoreChange),
    [contentId]
  );
  const getCollabManagerSnapshot = useCallback(
    () => collabStateManager.getCollabManager(contentId),
    [contentId]
  );
  const collabManager = useSyncExternalStore(
    subscribeToCollabManager,
    getCollabManagerSnapshot,
    () => undefined
  );
  const visibleEntryIds = defaultStoreUtils.useEqualSelector((state) => {
    const ignoredEntries = Array.from(editingIds).map(({ entryId }) => entryId);
    const database = selectDatabaseDraftByContentId(state, {
      contentId,
      viewSettings,
      includeDeleted: true,
      ignoredEntries
    });

    return database.entries;
  });
  const handleEligibleBackendAgentInteraction = useCallback(() => {
    manuallyFireExposureEvent('databaseBEStreaming');
  }, []);
  const analyticsClient = useAnalytics();
  const handleProgressiveEntryRevealLifecycleEvent = useCallback(
    (event: ProgressiveEntryRevealLifecycleEvent) => {
      void analyticsClient.sendDatabaseBEStreamingRevealEvent({
        ...event,
        source: analyticsSource,
        contentId,
        databaseContainer: getDatabaseContainerType()
      });
    },
    [analyticsClient, analyticsSource, contentId]
  );
  const progressiveEntryReveal = useProgressiveEntryReveal({
    collabManager,
    entryIds: visibleEntryIds,
    onEligibleBackendAgentEntryAddition: handleEligibleBackendAgentInteraction,
    onLifecycleEvent: handleProgressiveEntryRevealLifecycleEvent,
    resetKey: `${contentId}:${viewId}`
  });
  const progressiveEntryEditReveal = useProgressiveEntryEditReveal({
    collabManager,
    contentId,
    entryIds: visibleEntryIds,
    isPaused: progressiveEntryReveal.kind === 'revealing',
    onEligibleBackendAgentEntryEdit: handleEligibleBackendAgentInteraction,
    resetKey: `${contentId}:${viewId}`
  });
  const progressiveEntryDeleteReveal = useProgressiveEntryDeleteReveal({
    collabManager,
    contentId,
    entryIds: visibleEntryIds,
    isPaused:
      progressiveEntryReveal.kind === 'revealing' ||
      progressiveEntryEditReveal.kind === 'revealing',
    onEligibleBackendAgentEntryDelete: handleEligibleBackendAgentInteraction,
    resetKey: `${contentId}:${viewId}`
  });
  const progressiveFieldAddReveal = useProgressiveFieldAddReveal({
    collabManager,
    contentId,
    isPaused:
      progressiveEntryReveal.kind === 'revealing' ||
      progressiveEntryEditReveal.kind === 'revealing' ||
      progressiveEntryDeleteReveal.kind === 'revealing',
    layoutRoot,
    onEligibleBackendAgentFieldAdd: handleEligibleBackendAgentInteraction,
    resetKey: `${contentId}:${viewId}`
  });
  const progressiveFieldEditReveal = useBackendAgentFieldEditPresence({
    collabManager,
    contentId,
    isPaused:
      progressiveEntryReveal.kind === 'revealing' ||
      progressiveEntryEditReveal.kind === 'revealing' ||
      progressiveEntryDeleteReveal.kind === 'revealing' ||
      progressiveFieldAddReveal.kind === 'revealing',
    layoutRoot,
    onEligibleBackendAgentFieldEdit: handleEligibleBackendAgentInteraction,
    resetKey: `${contentId}:${viewId}`
  });
  const progressiveFieldDeleteReveal = useBackendAgentFieldDeleteOverlay({
    collabManager,
    contentId,
    isPaused:
      progressiveEntryReveal.kind === 'revealing' ||
      progressiveEntryEditReveal.kind === 'revealing' ||
      progressiveEntryDeleteReveal.kind === 'revealing' ||
      progressiveFieldAddReveal.kind === 'revealing' ||
      progressiveFieldEditReveal.kind === 'revealing',
    layoutRoot,
    onEligibleBackendAgentFieldDelete: handleEligibleBackendAgentInteraction,
    resetKey: `${contentId}:${viewId}`
  });
  const hasAdditionIndexMismatch =
    progressiveEntryReveal.kind === 'revealing' &&
    hasStructuralEntryRevealIndexMismatch(
      visibleEntryIds,
      progressiveEntryReveal.entryIds
    );
  useStructuralEntryRevealPresentation({
    isActive:
      hasAdditionIndexMismatch ||
      progressiveEntryDeleteReveal.kind === 'revealing' ||
      progressiveFieldDeleteReveal.kind === 'revealing',
    layoutRoot
  });
  const activeBackendAgentEntryReveal =
    progressiveEntryReveal.kind === 'revealing'
      ? progressiveEntryReveal
      : progressiveEntryEditReveal.kind === 'revealing'
        ? progressiveEntryEditReveal
        : progressiveEntryDeleteReveal.kind === 'revealing'
          ? progressiveEntryDeleteReveal
          : undefined;
  const activeBackendAgentEntryId = activeBackendAgentEntryReveal?.activeEntryId;
  const activeBackendAgentEntryParticipant = activeBackendAgentEntryReveal?.participant;
  useBackendAgentEntryPresence(
    activeBackendAgentEntryId,
    activeBackendAgentEntryParticipant
  );

  return (
    <ProgressiveFieldAddRevealProvider result={progressiveFieldAddReveal}>
      <ProgressiveFieldEditRevealProvider result={progressiveFieldEditReveal}>
        <ProgressiveFieldDeleteRevealProvider result={progressiveFieldDeleteReveal}>
          <ProgressiveEntryRevealProvider
            activeEntryId={activeBackendAgentEntryId}
            result={progressiveEntryReveal}
          >
            {children}
          </ProgressiveEntryRevealProvider>
        </ProgressiveFieldDeleteRevealProvider>
      </ProgressiveFieldEditRevealProvider>
    </ProgressiveFieldAddRevealProvider>
  );
}

function ProgressiveTimelineLayout(
  props: Omit<
    TimelineLayoutProps,
    'revealedEntryIds' | 'rovoStreamingState' | 'streamingEntryIdToScroll'
  >
) {
  const progressiveEntryRevealContext = useProgressiveEntryRevealContext();
  const {
    currentEntryOperation,
    currentFieldOperation,
    entryIdSelectedForProcessing,
    fieldIdSelectedForProcessing,
    isSingleFieldStreaming
  } = useSnapshot(aiFirstCreationStreamingState);

  return (
    <TimelineLayout
      {...props}
      revealedEntryIds={progressiveEntryRevealContext?.revealedEntryIds}
      rovoStreamingState={{
        currentEntryOperation,
        currentFieldOperation,
        entryId: entryIdSelectedForProcessing,
        fieldId: fieldIdSelectedForProcessing,
        isSingleFieldStreaming
      }}
      streamingEntryIdToScroll={progressiveEntryRevealContext?.activeEntryId}
    />
  );
}

const DatabaseLayoutRenderer = ({
  canEditContent,
  couldEditFields,
  canEditFields,
  contentId,
  isContentEditable = false,
  outerContainerRef,
  setOuterContainerRef
}: {
  contentId: string;
  canEditContent: boolean;
  couldEditFields: boolean;
  canEditFields: boolean;
  isContentEditable?: boolean;
  outerContainerRef: HTMLElement | null;
  setOuterContainerRef: (ref: HTMLElement | null) => void;
}) => {
  const addEntryButtonRef = useRef<HTMLButtonElement>(null);
  const scrollToAddedEntryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [innerContainerRef, setInnerContainerRef] = useState<HTMLElement | null>(null);
  const outerContainerSize = useResizeObserver(outerContainerRef, false);
  const innerContainerSize = useResizeObserver(innerContainerRef, false);
  const heightIsLessThanContainer =
    outerContainerSize !== undefined &&
    innerContainerSize !== undefined &&
    innerContainerSize.height <= outerContainerSize.height;
  const widthIsLessThanContainer =
    outerContainerSize === undefined ||
    innerContainerSize === undefined ||
    innerContainerSize.width <= outerContainerSize.width + SCROLL_WIDTH_TOLERANCE_PX;
  const [isConfluencePanelOpen, setIsConfluencePanelOpen] = useState<boolean>(false);
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const renderSection = useScrollPosition({
    isDisabled: !innerContainerRef,
    container: innerContainerRef ?? undefined,
    batchHeight: RENDER_BATCH_HEIGHT,
    margin: RENDER_MARGIN
  });
  const { view } = useView();
  const registry = getService('ViewLayoutRegistry');
  const persistedLayoutType = registry.isWritable(TABLE_V2_LAYOUT_ID)
    ? getService('DatabaseSdk').views.getViewLayoutType(view.id)
    : undefined;
  const ExtensionLayoutRenderer = resolveExtensionLayoutRenderer(
    registry,
    persistedLayoutType,
    extensionLayoutRenderers
  );
  const progressiveEntryRevealAnalyticsSource = getProgressiveEntryRevealAnalyticsSource(
    view.settings.layout.type
  );
  const isTimelineLayout = view.settings.layout.type === DatabaseLayoutType.TIMELINE;
  const isPinnedLeadingColumnsEnabled = getExperimentValue(
    'databasesPinnedLeadingColumns',
    'enabled',
    {
      fireExperimentExposure: false
    }
  );
  const shouldShowTimelineHeaderControls =
    isTimelineLayout &&
    getExperimentValue('databasesRoadmap', 'isEnabled', {
      fireExperimentExposure: false
    });

  useEffect(() => {
    const handleConfluenceSidebarState = async () => {
      const contentAPI = await waitForContentAPI();
      if (contentAPI?.confluenceAPI?.sidebar?.onConfluenceSidebarStateChange) {
        contentAPI.confluenceAPI.sidebar.onConfluenceSidebarStateChange((state) => {
          if (state.isOpen) {
            setIsConfluencePanelOpen(state.isOpen);
          }
        });
      }
    };
    handleConfluenceSidebarState();
  }, []);

  const { left } = useWindowScrollPosition({
    container: innerContainerRef?.parentElement ?? undefined
  });

  const { top } = useScrollPosition({
    isDisabled: !innerContainerRef,
    container: innerContainerRef ?? undefined
  });

  // When an entry is newly created, its main field value should be in editable state and focused.
  // TODO: NCTD-3740
  const [editingIds, setEditingIds] = useState<EditingIds>(new Set());

  const scrollToAddedEntry = useCallback(
    (getScrollLeft: (scrollContainer: HTMLElement) => number) => {
      if (scrollToAddedEntryTimeoutRef.current !== undefined) {
        clearTimeout(scrollToAddedEntryTimeoutRef.current);
      }
      scrollToAddedEntryTimeoutRef.current = setTimeout(() => {
        scrollToAddedEntryTimeoutRef.current = undefined;
        if (outerContainerRef && innerContainerRef) {
          outerContainerRef.scrollTo({
            left: getScrollLeft(outerContainerRef),
            top: innerContainerRef.getBoundingClientRect().height,
            behavior: 'smooth'
          });
        }
      }, 0);
    },
    [innerContainerRef, outerContainerRef]
  );

  useEffect(
    () => () => {
      if (scrollToAddedEntryTimeoutRef.current !== undefined) {
        clearTimeout(scrollToAddedEntryTimeoutRef.current);
      }
    },
    []
  );

  const handleEntry = useCallback(
    (entry: DatabaseEntry) => {
      requestAnimationFrame(() => {
        const selectionsApi = getService('SelectionsApi');
        const { fieldId } = selectionsApi.getValueIdsAtPosition({
          row: { index: 0 },
          col: { index: 0 }
        });
        if (fieldId) {
          selectionsApi.startEditing(entry.id, fieldId, 'table');
        }
      });

      scrollToAddedEntry((scrollContainer) =>
        getPinnedLeadingAddEntryScrollLeft({
          currentScrollLeft: scrollContainer.scrollLeft,
          isEnabled: isPinnedLeadingColumnsEnabled,
          isTableLayout: view.settings.layout.type === DatabaseLayoutType.TABLE,
          viewProperties: view.settings.properties
        })
      );
    },
    [
      isPinnedLeadingColumnsEnabled,
      scrollToAddedEntry,
      view.settings.layout.type,
      view.settings.properties
    ]
  );

  const handleTimelineEntryAdded = useCallback(() => {
    scrollToAddedEntry((scrollContainer) => scrollContainer.scrollLeft);
  }, [scrollToAddedEntry]);

  const isNativeTableEmbed =
    isNativeEmbedEditorPlugin() && view.settings.layout.type === DatabaseLayoutType.TABLE;
  // The editor keeps the first native embed height, so measure the table after Suspense resolves.
  const ContentResizeObserver = isNativeTableEmbed
    ? React.Fragment
    : LayoutResizeObserver;
  const TableResizeObserver = isNativeTableEmbed ? LayoutResizeObserver : React.Fragment;

  const content = (
    <DatabaseContentWrapper
      innerRef={setOuterContainerRef}
      heightIsLessThanContainer={heightIsLessThanContainer}
      widthIsLessThanContainer={
        isNativeEmbedEditorPlugin() ? widthIsLessThanContainer : undefined
      }
      allowOverheadSpace={false}
      hideScrollbar={isNativeEmbed()}
      autoScrollAxis={isTimelineLayout ? 'vertical' : undefined}
      isTableLayout={view.settings.layout.type === DatabaseLayoutType.TABLE}
    >
      <div
        className={`${styles.databaseContentInnerContainer} ${canEditContent ? styles.canEditContent : ''}`}
        id="database-content-inner-container"
        data-testid={'database-content-inner-container'}
        ref={setInnerContainerRef}
      >
        {isDBAiFirstCreationEnabled && outerContainerRef && (
          <InlineRovoPromptLoader container={outerContainerRef} />
        )}
        {ExtensionLayoutRenderer ? (
          <Suspense fallback={<DatabaseTableSkeletonRefresh />}>
            <ExtensionLayoutRenderer
              key={`${contentId}/${view.id}`}
              scrollElement={outerContainerRef}
            />
          </Suspense>
        ) : progressiveEntryRevealAnalyticsSource ? (
          <ProgressiveEntryRevealOwner
            analyticsSource={progressiveEntryRevealAnalyticsSource}
            contentId={contentId}
            editingIds={editingIds}
            layoutRoot={innerContainerRef}
            viewId={view.id}
            viewSettings={view.settings}
          >
            <ContentResizeObserver>
              {view.settings.layout.type === DatabaseLayoutType.GALLERY ? (
                outerContainerSize && (
                  <Suspense fallback={<DatabaseGallerySkeleton />}>
                    <GalleryLayout
                      canEditContent={canEditContent}
                      scrollContainerRef={outerContainerRef}
                      containerWidth={outerContainerSize.width}
                    />
                  </Suspense>
                )
              ) : view.settings.layout.type === DatabaseLayoutType.BOARD ? (
                outerContainerSize && (
                  <Suspense fallback={<DatabaseBoardSkeleton />}>
                    <BoardLayout
                      canEditFields={canEditFields}
                      canEditContent={canEditContent}
                      containerWidth={outerContainerSize.width}
                      containerRef={outerContainerRef}
                    />
                  </Suspense>
                )
              ) : isTimelineLayout ? (
                <Suspense fallback={<DatabaseTableSkeleton />}>
                  <ProgressiveTimelineLayout
                    contentId={contentId}
                    canEditContent={canEditContent}
                    canEditFields={canEditFields}
                    onEntryAdded={handleTimelineEntryAdded}
                    renderSection={renderSection}
                    scrollContainer={outerContainerRef}
                    scrollContainerWidth={outerContainerRef?.clientWidth}
                    shouldShowTimelineHeaderControls={shouldShowTimelineHeaderControls}
                  />
                </Suspense>
              ) : (
                outerContainerSize && (
                  <Suspense fallback={<DatabaseTableSkeletonRefresh />}>
                    <TableResizeObserver>
                      <TableLayout
                        editingIds={editingIds}
                        setEditingIds={setEditingIds}
                        contentId={contentId}
                        isContentEditable={isContentEditable}
                        canEditContent={canEditContent}
                        canEditFields={canEditFields}
                        couldEditFields={couldEditFields}
                        renderSection={renderSection}
                        containerWidth={outerContainerRef?.clientWidth ?? 0}
                        addEntryButtonRef={addEntryButtonRef}
                        containerTop={top}
                        containerLeft={left}
                        handleEntry={handleEntry}
                        isConfluencePanelOpen={isConfluencePanelOpen}
                      />
                    </TableResizeObserver>
                  </Suspense>
                )
              )}
            </ContentResizeObserver>
          </ProgressiveEntryRevealOwner>
        ) : (
          <LayoutResizeObserver>
            {outerContainerSize && (
              <Suspense fallback={<DatabaseTableSkeleton />}>
                <CalendarLayout
                  canEditFields={canEditFields}
                  canEditContent={canEditContent}
                  contentId={contentId}
                  renderSection={renderSection}
                  containerWidth={outerContainerRef?.clientWidth ?? 0}
                />
              </Suspense>
            )}
          </LayoutResizeObserver>
        )}
      </div>
    </DatabaseContentWrapper>
  );

  return (
    <AiSummaryCellHeightObserverProvider key={contentId}>
      <DatabaseAiConfigModalProvider contentId={contentId}>
        {isNativeEmbedEditorPlugin() ? (
          <NativeEmbedSelectionBorder>{content}</NativeEmbedSelectionBorder>
        ) : (
          content
        )}
      </DatabaseAiConfigModalProvider>
    </AiSummaryCellHeightObserverProvider>
  );
};

export default DatabaseLayoutRenderer;
